import React, { useEffect, useMemo, useState } from "react";
import Sidebar from "./Sidebar";
import Topbar from "./Topbar";
import { motion } from "framer-motion";
import {
  getCombinedData,
  getAnalytics,
  AppointmentsSummary,
  PaymentsSummary,
  AnalyticsResponse,
} from "../../api/dashboardService";
import { ToastContainer, toast } from "react-toastify";
// inline SVG icons to avoid JSX typing issues from icon libs
import { getUserProfile } from "../../api/user";
import { Chart } from "primereact/chart";
import { getAllEducationalResources } from "../../api/educationalResource";
import { getPlayableSource } from "../../utils/videoUrl";

const Dashboard: React.FC = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [appointmentsSummary, setAppointmentsSummary] =
    useState<AppointmentsSummary | null>(null);
  const [activePatientsCount, setActivePatientsCount] = useState<number | null>(
    null
  );
  const [paymentsSummary, setPaymentsSummary] =
    useState<PaymentsSummary | null>(null);
  const [analytics, setAnalytics] = useState<AnalyticsResponse | null>(null);
  const [doctorName, setDoctorName] = useState<string>("");

  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [videoModalLoading, setVideoModalLoading] = useState(false);
  const [videoModalError, setVideoModalError] = useState<string | null>(null);
  const [videoItems, setVideoItems] = useState<any[]>([]);
  const [videoCategory, setVideoCategory] = useState<string>("");
  const [activeVideo, setActiveVideo] = useState<any | null>(null);
  const [videoPreviewOpen, setVideoPreviewOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  // Default notifications (dummy data)
  const defaultNotifications = useMemo(() => [
    { id: 1, title: "Notification title", message: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Donec eu lorem at nunc blandit aliquet.", time: "Just now" },
    { id: 2, title: "Notification title", message: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Donec eu lorem at nunc blandit aliquet.", time: "Just now" },
    { id: 3, title: "Notification title", message: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Donec eu lorem at nunc blandit aliquet.", time: "Just now" },
    { id: 4, title: "Notification title", message: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Donec eu lorem at nunc blandit aliquet.", time: "Just now" },
  ], []);

  const [notifications, setNotifications] = useState(defaultNotifications);

  // Reset notifications when modal opens (since it's dummy data)
  useEffect(() => {
    if (notificationsOpen) {
      setNotifications(defaultNotifications);
    }
  }, [notificationsOpen, defaultNotifications]);

  const now = useMemo(() => new Date(), []);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1); // 1-12
  const greeting = useMemo(() => {
    const hour = now.getHours();
    if (hour < 12) return "Good Morning 👋";
    if (hour < 17) return "Good Afternoon 👋";
    return "Good Evening 👋";
  }, [now]);
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const handlePrevMonth = () => {
    setSelectedMonth((m) => {
      if (m > 1) return m - 1;
      setSelectedYear((y) => y - 1);
      return 12;
    });
  };
  const handleNextMonth = () => {
    setSelectedMonth((m) => {
      if (m < 12) return m + 1;
      setSelectedYear((y) => y + 1);
      return 1;
    });
  };

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [combinedRes, analyticsRes, profileRes] = await Promise.all([
          getCombinedData(),
          getAnalytics(selectedYear, selectedMonth),
          getUserProfile(),
        ]);

        const combined = combinedRes?.data?.data;
        const analyticsData = analyticsRes?.data?.data;
        const profile = profileRes?.data?.data || profileRes?.data;

        if (isMounted) {
          setAppointmentsSummary(combined?.appointmentsSummary || null);
          setActivePatientsCount(combined?.activePatientsCount ?? null);
          setPaymentsSummary(combined?.paymentsSummary || null);
          setAnalytics(analyticsData || null);
          setDoctorName(profile?.name || profile?.fullName || "");
        }
      } catch (err: any) {
        console.error(err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    load();
    return () => {
      isMounted = false;
    };
  }, [selectedMonth, selectedYear]);

  const SkeletonCard = () => (
    <div className="rounded-3xl shadow-2xl bg-white p-6 animate-pulse">
      <div className="h-4 bg-gray-200 rounded w-1/3 mb-4" />
      <div className="h-8 bg-gray-200 rounded w-1/4 mb-6" />
      <div className="h-3 bg-gray-200 rounded w-full mb-2" />
      <div className="h-3 bg-gray-200 rounded w-5/6" />
    </div>
  );

  const formatCategory = (raw?: string) => {
    if (!raw) return "—";
    const normalized = raw.toLowerCase().replace(/_/g, " ");
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  };

  const openVideoModal = async () => {
    setVideoModalOpen(true);
    setVideoModalLoading(true);
    setVideoModalError(null);
    try {
      const res = await getAllEducationalResources();
      const data = res?.data?.data || res?.data || [];
      setVideoItems(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setVideoItems([]);
      setVideoModalError(
        err?.response?.data?.message || "Failed to load educational videos"
      );
    } finally {
      setVideoModalLoading(false);
    }
  };

  const closeVideoModal = () => {
    setVideoModalOpen(false);
    setActiveVideo(null);
    setVideoCategory("");
  };

  const filteredVideos = useMemo(() => {
    if (!videoCategory) return videoItems;
    return videoItems.filter((v) => v.category === videoCategory);
  }, [videoItems, videoCategory]);

  const newPatientsChartData = useMemo(() => {
    if (!analytics) return null;
    const categories = analytics.newPatients.categories || [];
    const data = analytics.newPatients.series?.[0]?.data || [];
    return {
      labels: categories,
      datasets: [
        {
          label: "New Patients",
          data,
          backgroundColor: "#3b82f6",
          borderRadius: 6,
        },
      ],
    };
  }, [analytics]);

  const newPatientsChartOptions: any = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "bottom",
          labels: {
            boxWidth: 10,
          },
        },
        tooltip: {
          enabled: true,
          callbacks: {
            label: (ctx: any) => `New Patients: ${ctx.parsed.y}`,
          },
        },
      },
      scales: {
        x: {
          ticks: {
            color: "#6b7280",
            maxRotation: 0,
            autoSkip: true,
          },
          grid: {
            display: false,
          },
        },
        y: {
          ticks: {
            color: "#9ca3af",
            stepSize: 1,
          },
          grid: {
            color: "#eef2f7",
          },
        },
      },
    }),
    []
  );

  const appointmentsChartData = useMemo(() => {
    if (!analytics) return null;
    const categories = analytics.appointmentsOverview.categories || [];
    const series = analytics.appointmentsOverview.series || [];
    const colors = ["#3b82f6", "#10b981", "#ef4444"];
    return {
      labels: categories,
      datasets: series.map((s, idx) => ({
        label: s.name,
        data: s.data,
        borderColor: colors[idx % colors.length],
        backgroundColor: colors[idx % colors.length],
        fill: false,
        tension: 0.3,
        pointRadius: 3,
      })),
    };
  }, [analytics]);

  const appointmentsChartOptions: any = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "bottom",
        },
        tooltip: {
          enabled: true,
        },
      },
      scales: {
        x: {
          ticks: {
            color: "#6b7280",
          },
          grid: {
            display: false,
          },
        },
        y: {
          ticks: {
            color: "#9ca3af",
          },
          grid: {
            color: "#eef2f7",
          },
        },
      },
    }),
    []
  );

  const ProgressBar: React.FC<{ value: number }> = ({ value }) => (
    <div className="w-full bg-gray-100 rounded-full h-3">
      <div
        className="h-3 rounded-full"
        style={{
          width: `${Math.min(Math.max(value, 0), 100)}%`,
          background: "linear-gradient(90deg, #2563eb, #10b981)",
        }}
      />
    </div>
  );

  return (
    <div className="flex h-screen bg-gray-100">
      <ToastContainer position="top-right" autoClose={3000} />
      <Sidebar isOpen={isSidebarOpen} toggle={() => setIsSidebarOpen(!isSidebarOpen)} />
      <div className="flex-1 flex flex-col">
        <Topbar toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
        <main className="p-4 md:p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="w-full max-w-6xl mx-auto"
          >
            <div className="mb-6 flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">{greeting}</p>
                <h1 className="text-xl md:text-2xl font-bold text-sky-700">{doctorName ? `Dr. ${doctorName}` : "Dr."}</h1>
              </div>
              <div className="flex items-center gap-3">
                <button
                  className="w-10 h-10 rounded-full bg-white shadow-2xl flex items-center justify-center hover:shadow-xl hover:scale-105 transition-transform"
                  title="Video Tutorials"
                  onClick={openVideoModal}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" strokeWidth="1.8" className="w-6 h-6 text-sky-600" stroke="currentColor">
                    <circle cx="12" cy="12" r="9" />
                    <path d="M10 8l6 4-6 4V8z" fill="currentColor" stroke="none" />
                  </svg>
                </button>
                {/* <button
                  className="relative w-10 h-10 rounded-full bg-white shadow-2xl flex items-center justify-center hover:shadow-xl hover:scale-105 transition-transform"
                  title="Notifications"
                  onClick={() => setNotificationsOpen(true)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" strokeWidth="1.8" className="w-6 h-6 text-sky-600" stroke="currentColor">
                    <path d="M15 17h5l-1.4-1.4A2 2 0 0 1 18 14.2V11a6 6 0 0 0-12 0v3.2c0 .5-.2 1-.6 1.4L4 17h5" />
                    <path d="M9 17a3 3 0 0 0 6 0" />
                  </svg>
                  <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full" />
                </button> */}
              </div>
            </div>

            {/* Top Stats Cards */}
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <SkeletonCard />
                <SkeletonCard />
                <SkeletonCard />
              </div>
            ) : error ? (
              <div className="rounded-2xl bg-white p-6 shadow-2xl border border-red-100">
                <p className="text-red-600">{error}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Appointments Summary */}
                <div className="rounded-3xl shadow-2xl bg-white p-6 transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_18px_40px_rgba(15,23,42,0.12)]">
                  <h3 className="text-gray-700 text-sm font-medium mb-2">Appointments</h3>
                  <div className="flex items-end justify-between">
                    <div>
                      <div className="text-3xl font-bold text-gray-800">
                        {appointmentsSummary?.totalAppointments ?? 0}
                      </div>
                      <div className="text-xs text-gray-500">Total</div>
                    </div>
                    <div className="text-right">
                      <div className="text-green-600 text-sm font-semibold">
                        {appointmentsSummary?.completedAppointments ?? 0} Completed
                      </div>
                      <div className="text-orange-500 text-sm font-semibold">
                        {appointmentsSummary?.remainingAppointments ?? 0} Remaining
                      </div>
                    </div>
                  </div>
                  <div className="mt-4">
                    <ProgressBar value={appointmentsSummary?.progressPercentage ?? 0} />
                    <div className="text-xs text-gray-500 mt-1">
                      Progress: {appointmentsSummary?.progressPercentage ?? 0}%
                    </div>
                  </div>
                </div>

                {/* Active Patients */}
                <div className="rounded-3xl shadow-2xl bg-white p-6 transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_18px_40px_rgba(15,23,42,0.12)]">
                  <h3 className="text-gray-700 text-sm font-medium mb-2">Active Patients</h3>
                  <div className="text-3xl font-bold text-gray-800">
                    {activePatientsCount ?? 0}
                  </div>
                  <div className="text-xs text-gray-500">Currently active</div>
                </div>

                {/* Payments Summary */}
                <div className="rounded-3xl shadow-2xl bg-white p-6 transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_18px_40px_rgba(15,23,42,0.12)]">
                  <h3 className="text-gray-700 text-sm font-medium mb-3">Payments</h3>
                  <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                    <span>Pending</span>
                    <span>
                      {(paymentsSummary?.pendingPayments ?? 0).toString().padStart(2, "0")} / {(paymentsSummary?.totalPayments ?? 0).toString().padStart(2, "0")}
                    </span>
                  </div>
                  <ProgressBar value={paymentsSummary?.progressPercentage ?? 0} />
                </div>
              </div>
            )}

            {/* Analytics Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              {loading ? (
                <>
                  <SkeletonCard />
                  <SkeletonCard />
                </>
              ) : (
                <>
                  {/* New Patients */}
                  <div className="rounded-3xl shadow-2xl bg-white p-6 transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_18px_40px_rgba(15,23,42,0.12)]">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <h3 className="text-gray-700 text-sm font-medium">
                          Monthly New Patients
                        </h3>
                        <span
                          className="text-xs text-gray-400 cursor-help"
                          title="Bar chart of new patients for the selected month. Hover over a bar to see exact values."
                        >
                          ⓘ
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <button
                          className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center"
                          onClick={handlePrevMonth}
                          aria-label="Previous month"
                        >
                          ‹
                        </button>
                        <span className="min-w-[72px] text-center text-sm font-medium">
                          {monthNames[selectedMonth - 1]}
                        </span>
                        <button
                          className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center"
                          onClick={handleNextMonth}
                          aria-label="Next month"
                        >
                          ›
                        </button>
                      </div>
                    </div>
                    {analytics && newPatientsChartData ? (
                      <div className="w-full overflow-x-auto">
                        <div className="min-w-[720px] h-80">
                          <Chart
                            type="bar"
                            data={newPatientsChartData}
                            options={newPatientsChartOptions}
                            style={{ height: "100%" }}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="text-gray-500 text-sm">No data</div>
                    )}
                  </div>

                  {/* Appointments Overview */}
                  <div className="rounded-3xl shadow-2xl bg-white p-6 transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_18px_40px_rgba(15,23,42,0.12)]">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <h3 className="text-gray-700 text-sm font-medium">
                          Appointments Overview{" "}
                          {analytics?.appointmentsOverview?.monthLabel
                            ? `- ${analytics.appointmentsOverview.monthLabel}`
                            : ""}
                        </h3>
                        <span
                          className="text-xs text-gray-400 cursor-help"
                          title="Line chart of completed, postponed and cancelled appointments. Hover over points to see details."
                        >
                          ⓘ
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <button
                          className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center"
                          onClick={handlePrevMonth}
                          aria-label="Previous month"
                        >
                          ‹
                        </button>
                        <span className="min-w-[72px] text-center text-sm font-medium">
                          {monthNames[selectedMonth - 1]}
                        </span>
                        <button
                          className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center"
                          onClick={handleNextMonth}
                          aria-label="Next month"
                        >
                          ›
                        </button>
                      </div>
                    </div>
                    {analytics && appointmentsChartData ? (
                      <div className="w-full overflow-x-auto">
                        <div className="min-w-[720px] h-80">
                          <Chart
                            type="line"
                            data={appointmentsChartData}
                            options={appointmentsChartOptions}
                            style={{ height: "100%" }}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="text-gray-500 text-sm">No data</div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Payments Progress Visualization */}
            <div className="mt-6">
              {loading ? (
                <SkeletonCard />
              ) : (
                <div className="rounded-3xl shadow-2xl bg-white p-6 transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_18px_40px_rgba(15,23,42,0.12)]">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-gray-700 text-sm font-medium">Payments Progress</h3>
                    <span className="text-xs text-gray-500">
                      {analytics?.payments?.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="relative w-28 h-28">
                      <svg viewBox="0 0 36 36" className="w-28 h-28">
                        <path
                          className="text-gray-200"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                          d="M18 2a16 16 0 1 1 0 32 16 16 0 1 1 0-32"
                        />
                        <path
                          strokeLinecap="round"
                          strokeWidth="4"
                          fill="none"
                          stroke="url(#grad)"
                          strokeDasharray={`${Math.round((analytics?.payments?.progress || 0) * 100)}, 100`}
                          d="M18 2a16 16 0 1 1 0 32 16 16 0 1 1 0-32"
                        />
                        <defs>
                          <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#2563eb" />
                            <stop offset="100%" stopColor="#10b981" />
                          </linearGradient>
                        </defs>
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-lg font-semibold text-gray-800">
                          {Math.round((analytics?.payments?.progress || 0) * 100)}%
                        </div>
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                          <div className="text-2xl font-bold text-gray-800">{analytics?.payments?.total ?? 0}</div>
                          <div className="text-xs text-gray-500">Total</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-green-600">{(analytics?.payments?.total || 0) - (analytics?.payments?.pending || 0)}</div>
                          <div className="text-xs text-gray-500">Completed</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-orange-500">{analytics?.payments?.pending ?? 0}</div>
                          <div className="text-xs text-gray-500">Pending</div>
                        </div>
                      </div>
                      <div className="mt-4">
                        <ProgressBar value={Math.round((analytics?.payments?.progress || 0) * 100)} />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {videoModalOpen && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl p-6 max-h-[80vh] flex flex-col"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800">
                        Educational Hub
                      </h3>
                      <p className="text-xs text-gray-500">
                        Watch educational videos by category
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={closeVideoModal}
                      className="text-gray-500 hover:text-gray-700 text-xl leading-none"
                      aria-label="Close"
                    >
                      ×
                    </button>
                  </div>

                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">Category:</span>
                      <select
                        value={videoCategory}
                        onChange={(e) => setVideoCategory(e.target.value)}
                        className="px-6 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-sm"
                      >
                        <option value="">All categories</option>
                        <option value="BRACES">Braces</option>
                        <option value="ALIGNERS">Aligners</option>
                        <option value="PROCEDURES">Procedures</option>
                        <option value="DENTAL_CARE">Dental care</option>
                        <option value="RECOMMENDATIONS">Recommendations</option>
                      </select>
                    </div>
                    <div className="text-xs text-gray-500">
                      Total videos:{" "}
                      <span className="font-semibold">
                        {filteredVideos.length}
                      </span>
                    </div>
                  </div>

                  <div className="flex-1 border rounded-xl p-3 overflow-y-auto">
                    {videoModalLoading ? (
                      <div className="text-sm text-gray-500">Loading...</div>
                    ) : videoModalError ? (
                      <div className="text-sm text-red-500">
                        {videoModalError}
                      </div>
                    ) : filteredVideos.length === 0 ? (
                      <div className="text-sm text-gray-500">
                        No videos found for this category.
                      </div>
                    ) : (
                      <ul className="space-y-3 text-sm">
                        {filteredVideos.map((vid) => (
                          <li
                            key={vid.id}
                            className="border rounded-lg p-3 hover:bg-gray-50 cursor-pointer"
                            onClick={() => {
                              setActiveVideo(vid);
                              setVideoPreviewOpen(true);
                            }}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <div className="font-semibold text-gray-800">
                                  {vid.title}
                                </div>
                                <div className="text-[11px] text-blue-600 uppercase tracking-wide mt-0.5">
                                  {formatCategory(vid.category)}
                                </div>
                                {vid.description && (
                                  <p className="mt-1 text-xs text-gray-600 line-clamp-2">
                                    {vid.description}
                                  </p>
                                )}
                              </div>
                              <button
                                type="button"
                                className="text-xs px-3 py-1 rounded-full bg-sky-600 text-white hover:bg-sky-700"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActiveVideo(vid);
                                  setVideoPreviewOpen(true);
                                }}
                              >
                                Play
                              </button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </motion.div>
              </div>
            )}

            {notificationsOpen && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 max-h-[70vh] flex flex-col"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-800">
                      Notifications
                    </h3>
                    <button
                      type="button"
                      onClick={() => setNotificationsOpen(false)}
                      className="text-gray-500 hover:text-gray-700 text-xl leading-none"
                      aria-label="Close notifications"
                    >
                      ×
                    </button>
                  </div>
                  <div className="space-y-3 text-sm text-gray-700 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="text-center py-8 text-gray-500 text-sm">
                        No notifications
                      </div>
                    ) : (
                      notifications.map((notification) => (
                        <div
                          key={notification.id}
                          className="border border-gray-200 rounded-xl p-3 hover:bg-gray-50 transition-colors relative"
                        >
                          <button
                            type="button"
                            onClick={() => {
                              setNotifications(notifications.filter((n) => n.id !== notification.id));
                            }}
                            className="absolute top-2 right-2 text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50 transition-colors"
                            aria-label="Delete notification"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-4 w-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                          </button>
                          <div className="flex items-center justify-between mb-1 pr-6">
                            <span className="font-semibold">
                              {notification.title}
                            </span>
                            <span className="text-[10px] text-gray-400">
                              {notification.time}
                            </span>
                          </div>
                          <p className="text-xs text-gray-600">
                            {notification.message}
                          </p>
                        </div>
                      ))
                    )}
                  </div>
                </motion.div>
              </div>
            )}

            {videoPreviewOpen && activeVideo && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-4"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="text-base font-semibold text-gray-800">
                        {activeVideo.title}
                      </h3>
                      <p className="text-[11px] text-blue-600">
                        {formatCategory(activeVideo.category)}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setVideoPreviewOpen(false)}
                      className="text-gray-500 hover:text-gray-700 text-xl leading-none"
                      aria-label="Close video"
                    >
                      ×
                    </button>
                  </div>
                  {(() => {
                    const src = getPlayableSource(activeVideo);
                    if (!src) {
                      return (
                        <div className="flex items-center justify-center h-40 text-xs text-gray-500 border border-dashed border-gray-300 rounded-lg">
                          No video URL found for this resource.
                        </div>
                      );
                    }
                    return src.type === "youtube" ? (
                      <div
                        className="relative w-full rounded-xl overflow-hidden bg-black"
                        style={{ paddingTop: "56.25%" }}
                      >
                        <iframe
                          key={activeVideo.id}
                          className="absolute inset-0 w-full h-full"
                          src={`${src.url}?autoplay=1&rel=0`}
                          title={activeVideo.title}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                    ) : (
                      <video
                        key={activeVideo.id}
                        controls
                        autoPlay
                        className="w-full rounded-xl bg-black max-h-[60vh]"
                        src={src.url}
                      />
                    );
                  })()}
                </motion.div>
              </div>
            )}
          </motion.div>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
